import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  Button,
} from '@mui/material';
import {
  PlmPartner,
  EnumPlmPartnerPartnerType,
} from '../../../../types/generated-types';
import BorderedSection from '../../shared/borderedSection';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import '../ui/plm.css';

function formatPlmPartnerTypeLabel(
  value: EnumPlmPartnerPartnerType | string | null | undefined,
): string {
  if (value === EnumPlmPartnerPartnerType.Utility || value === 'utility') {
    return 'Utility';
  }
  if (
    value === EnumPlmPartnerPartnerType.Aggregator ||
    value === 'aggregator'
  ) {
    return 'Aggregator';
  }
  return value ? String(value) : '—';
}

interface PlmPartnerDetailViewerProps {
  plmPartner?: Partial<PlmPartner> | null;
  loading?: boolean;
  error?: Error;
}

export const PlmPartnerDetailViewer: React.FC<PlmPartnerDetailViewerProps> = ({
  plmPartner,
  loading,
  error,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Typography variant="h4" color="secondary">
          Preparing your data..
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">
          Error loading PLM partner: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!plmPartner) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography>No PLM partner found</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Tooltip title="Back to List">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/plmPartners')}
          >
            Back to PLM Partners
          </Button>
        </Tooltip>
        {can('update', 'PlmPartner') && (
          <Tooltip title="Edit">
            <IconButton
              onClick={() => navigate(`/plmPartners/${id}/edit-plmPartner`)}
              sx={{ ml: 2 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <BorderedSection
            title="PLM Partner Information"
            ccStyle={{ margin: '4px' }}
          >
            <Grid container className="plm-inner-grid-container">
              <Grid item xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <b>Name:</b>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={plmPartner.name}
                    />
                  </ListItem>
                  <ListItem>
                    <b>Description:</b>
                    {plmPartner.description && (
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plmPartner.description}
                      />
                    )}
                  </ListItem>
                  <ListItem>
                    <b>Partner type:</b>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={formatPlmPartnerTypeLabel(
                        plmPartner.partnerType,
                      )}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Contact Information">
            <Grid container className="plm-inner-grid-container">
              <Grid item xs={12}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <b>Contact Name:</b>
                    {plmPartner.contact?.name && (
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plmPartner.contact?.name || 'N/A'}
                      />
                    )}
                  </ListItem>
                  <ListItem>
                    <b>Email:</b>
                    {plmPartner.contact?.email && (
                      <a
                        className="plm-email"
                        href={`mailto:${plmPartner.contact.email}`}
                      >
                        <ListItemText
                          className="plm-listItem-text"
                          primary={plmPartner.contact.email}
                        />
                      </a>
                    )}
                  </ListItem>
                  <ListItem>
                    <b>Phone:</b>
                    {plmPartner.contact?.phone && (
                      <a
                        className="plm-email"
                        href={`tel:${plmPartner.contact.phone}`}
                      >
                        <ListItemText
                          className="plm-listItem-text"
                          primary={plmPartner.contact.phone}
                        />
                      </a>
                    )}
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>
      </Grid>
    </Box>
  );
};
